import { useMemo } from 'react';
import { useAuth } from '../components/DashboardComercial/useAuth';
import permissionService from '../services/permissionService';

/**
 * Hook para controlar permisos y perfiles en el frontend.
 * 
 * Centraliza la lógica de RBAC para que los componentes no necesiten
 * conocer los detalles de perfiles ni permisos.
 * 
 * Uso:
 * const { can, canUploadFiles, canViewPriority } = usePermissions();
 */
export function usePermissions() {
  const { user } = useAuth();
  
  const email = user?.email || '';
  
  const permissions = useMemo(() => {
    const perms = permissionService.getPermissions(email);
    
    // Función helper para verificar permisos
    const hasPermission = (permission) => perms.includes(permission);
    
    // Helper para verificar perfil
    const profile = permissionService.getProfile(email);
    
    return {
      profile,
      permissions: perms,
      hasPermission,
      // Helpers de perfil
      isManager: profile === 'manager',
      isUser: profile === 'user',
      isViewer: profile === 'viewer',
      // Permisos específicos
      canViewDashboard: hasPermission('DASHBOARD_VIEW'),
      canUpdateDashboard: hasPermission('DASHBOARD_UPDATE'),
      canViewPriority: hasPermission('PRIORITY_VIEW'),
      canUpdatePriority: hasPermission('PRIORITY_UPDATE'),
      canViewCatalog: hasPermission('CATALOG_VIEW'),
      canUpdateCatalog: hasPermission('CATALOG_UPDATE'),
      canUploadFiles: hasPermission('FILE_UPLOAD'),
      canDownloadReports: hasPermission('REPORT_DOWNLOAD'),
      canAssignClient: hasPermission('CLIENT_ASSIGN'),
    };
  }, [email]);
  
  return {
    // Perfil
    profile: permissions.profile,
    isManager: permissions.isManager,
    isUser: permissions.isUser,
    isViewer: permissions.isViewer,
    // Método genérico
    can: permissions.hasPermission,
    // Permisos específicos
    canViewDashboard: permissions.canViewDashboard,
    canUpdateDashboard: permissions.canUpdateDashboard,
    canViewPriority: permissions.canViewPriority,
    canUpdatePriority: permissions.canUpdatePriority,
    canViewCatalog: permissions.canViewCatalog,
    canUpdateCatalog: permissions.canUpdateCatalog,
    canUploadFiles: permissions.canUploadFiles,
    canDownloadReports: permissions.canDownloadReports,
    canAssignClient: permissions.canAssignClient,
  };
}

export default usePermissions;
